import React, { useEffect, useRef, useState } from 'react';
import ReactSelect, {
    ActionMeta,
    OnChangeValue,
    OptionProps,
    components,
    MultiValue,
    SingleValueProps,
    ValueContainerProps,
    MenuProps,
} from 'react-select';
import { useField } from 'formik';
import { BaseImage, IBaseImageProps } from '@alterpage/gatsby-plugin-image';

import {
    container,
    select,
    required,
    disabled,
    selectBox,
    hasIcon,
    iconSvg,
    iconBox,
    imageOptions,
    error,
    errorOutput,
    ratio,
    optionBox,
    optionCheckbox,
    optionCheckmark,
    selected,
    valueCount,
    radio,
    buttons,
    button,
    buttonClear,
    buttonApply,
} from './select.module.scss';

import CheckmarkIcon from '../../assets/images/svg/check.svg';

import { IOption } from '../../models/option.model';
import { IError } from '../../utils/get-form-errors';
import { TRequiredStarPlacement } from '../../models/form-field.model';
import { getSelectValue } from '../../utils/get-select-value';
import useTranslations from '../../hooks/use-translations';

import Error from './error';
import InputLabel from './input-label';

interface ISelectProps {
    className?: string;
    label?: string;
    name: string;
    options: IOption[];
    isMulti?: boolean;
    placeholder?: string;
    isSearchable?: boolean;
    isClearable?: boolean;
    icon?: React.SVGFactory;
    closeMenuOnSelect?: boolean;
    isRequired?: boolean;
    isDisabled?: boolean;
    isRadio?: boolean;
    requiredPlacement?: TRequiredStarPlacement[];
    hideSelectedOptions?: boolean;
    submitErrors?: IError[];
    handleSubmitErrors?: (errors: IError[]) => void;
    showError?: boolean;
    hint?: string;
    /** Zaznaczenia w trybie wielokrotnym jeszcze przed „Apply" (wartość pola zmienia się dopiero po nim). */
    onPendingValuesChange?: (values: string[]) => void;
    imageWithLabel?: boolean;
    baseImageProps?: Pick<
        IBaseImageProps,
        'objectPosition' | 'objectFit' | 'ratioClass' | 'aspectRatio'
    >;
}

interface ExtendedMenuProps extends MenuProps<IOption> {
    onApply: () => void;
    onClear: () => void;
}

interface ExtendedOptionsProps extends OptionProps<IOption> {
    imageWithLabel: boolean;
    baseImageProps?: Pick<
        IBaseImageProps,
        'objectPosition' | 'objectFit' | 'ratioClass' | 'aspectRatio'
    >;
    isRadio?: boolean;
    localValues?: (string | number | boolean)[];
}

interface ExtendedSingleValueProps extends SingleValueProps<IOption> {
    imageWithLabel: boolean;
    baseImageProps?: Pick<
        IBaseImageProps,
        'objectPosition' | 'objectFit' | 'ratioClass' | 'aspectRatio'
    >;
}

const Select: React.FC<ISelectProps> = ({
    className = '',
    name,
    options,
    hint,
    label,
    placeholder,
    icon,
    closeMenuOnSelect,
    isMulti = false,
    isSearchable = false,
    isClearable = false,
    isRequired = false,
    isDisabled = false,
    isRadio = false,
    hideSelectedOptions = false,
    submitErrors,
    handleSubmitErrors,
    showError = true,
    imageWithLabel = true,
    baseImageProps,
    onPendingValuesChange,
}) => {
    const [, meta, helpers] = useField(name);
    const t = useTranslations('Select');
    const submitError =
        submitErrors && submitErrors.length > 0 && submitErrors.find((e) => e.field === name);
    const hasError =
        !!(meta.error && meta.touched) ||
        (!!submitError && !!meta.value && meta.value.length === 0);
    const Icon = icon;
    const [localValues, setLocalValues] = useState<(string | number | boolean)[]>([]);
    const selectRef = useRef<any>(null);
    const onPendingValuesChangeRef = useRef(onPendingValuesChange);
    onPendingValuesChangeRef.current = onPendingValuesChange;

    const localPlaceholder = placeholder || t.defaultPlaceholder;
    const hasImageOptions = options.find((option) => option.imgUrl);

    const handleChange = (
        option: OnChangeValue<IOption, typeof isMulti> | null,
        actionMeta: ActionMeta<IOption>
    ) => {
        const { action } = actionMeta;
        if (action === 'clear') {
            const newValue = isMulti ? [] : '';
            helpers.setValue(newValue);
            return;
        }
        if (action === 'select-option' && option) {
            const newValue = assertIsMultiValue(option)
                ? option.map(({ value }) => value)
                : option.value;

            if (isMulti && actionMeta.option) {
                handleMultiSelectOption(actionMeta.option);
            } else {
                helpers.setValue(newValue);
            }

            return;
        }
        if (
            (action === 'remove-value' || action === 'deselect-option') &&
            assertIsMultiValue(option)
        ) {
            const newValue = option.map(({ value }) => value);

            if (isMulti && actionMeta.option) {
                handleMultiSelectOption(actionMeta.option);
            } else {
                helpers.setValue(newValue);
            }
            return;
        }
    };

    const handleMultiSelectOption = (option: IOption) => {
        if (localValues.includes(option.value.toString())) {
            const newValues = localValues.filter((value) => value !== option.value.toString());
            setLocalValues(newValues);
        } else {
            setLocalValues([...localValues, option.value.toString()]);
        }
    };

    const handleApplyValues = () => {
        helpers.setValue(localValues);
        selectRef.current?.onMenuClose();
    };

    const handleClearValues = () => {
        setLocalValues([]);
        helpers.setValue([]);
        selectRef.current?.onMenuClose();
    };

    useEffect(() => {
        if (submitErrors && submitError && handleSubmitErrors && meta.value && meta.touched) {
            handleSubmitErrors(submitErrors.filter((e) => e !== submitError));
        }
        if (Array.isArray(meta.value)) {
            // zaznaczenia w menu porównują się po `option.value.toString()`, więc wartości
            // liczbowe (np. identyfikatory z API) muszą wejść tu w tej samej postaci,
            // inaczej opcja nie wychodzi zaznaczona i da się ją dodać drugi raz
            setLocalValues(meta.value.map(String));
        }
    }, [meta.value]);

    useEffect(() => {
        if (!isMulti) return;
        onPendingValuesChangeRef.current?.(localValues.map(String));
    }, [isMulti, localValues]);

    return (
        <div
            className={`
            ${container} 
            ${className}
            ${!label && isRequired ? required : ''}
            ${isDisabled ? disabled + ' disabled' : ''}
            ${hasError ? error : ''} ${name}`}
            data-formik-name={name}
        >
            {label && (
                <InputLabel
                    hint={hint}
                    isDisabled={isDisabled}
                    isRequired={isRequired}
                    isError={hasError}
                >
                    {label}
                </InputLabel>
            )}
            <div className={`${selectBox} ${icon ? hasIcon : ''} select__container`}>
                {Icon && (
                    <div className={`${iconBox} select__icon-box`}>
                        <Icon className={`${iconSvg} select__icon`} />
                    </div>
                )}
                <ReactSelect
                    className={`${select} ${hasImageOptions ? imageOptions : ''} select__select`}
                    options={options.map((option) => ({ ...option, isDisabled: !option.enabled }))}
                    ref={selectRef}
                    classNamePrefix="react-select"
                    onChange={handleChange}
                    value={getSelectValue(meta.value, options, isMulti)}
                    isClearable={isClearable}
                    isMulti={isMulti}
                    isDisabled={isDisabled}
                    components={{
                        Menu: isMulti
                            ? (props) => (
                                  <CustomMenu
                                      {...props}
                                      onApply={handleApplyValues}
                                      onClear={handleClearValues}
                                  >
                                      {props.children}
                                  </CustomMenu>
                              )
                            : (props) => <components.Menu {...props} />,
                        Option: (props) => (
                            <CustomOption
                                imageWithLabel={imageWithLabel}
                                baseImageProps={baseImageProps}
                                isRadio={isRadio}
                                localValues={localValues}
                                {...props}
                            >
                                {props.children}
                            </CustomOption>
                        ),
                        SingleValue: (props) => (
                            <CustomSingleValue
                                imageWithLabel={imageWithLabel}
                                baseImageProps={baseImageProps}
                                {...props}
                            >
                                {props.children}
                            </CustomSingleValue>
                        ),
                        ValueContainer:
                            isMulti || isRadio
                                ? (props) => (
                                      //@ts-ignore
                                      <CustomValueContainer {...props}>
                                          {props.children}
                                      </CustomValueContainer>
                                  )
                                : (props) => <components.ValueContainer {...props} />,
                    }}
                    placeholder={localPlaceholder}
                    hideSelectedOptions={hideSelectedOptions}
                    isSearchable={isSearchable}
                    closeMenuOnSelect={closeMenuOnSelect}
                    noOptionsMessage={() => t.defaultNoOptions}
                    blurInputOnSelect={false}
                    controlShouldRenderValue={!isMulti}
                />
            </div>
            {showError && <Error name={name} />}
            {hasError && submitError && <p className={errorOutput}>{submitError.content}</p>}
        </div>
    );
};

const CustomMenu = (props: ExtendedMenuProps) => {
    const { onClear, onApply } = props;

    return (
        <components.Menu {...props}>
            {props.children}
            <div className={buttons}>
                <button onClick={onClear} className={`${button} ${buttonClear}`}>
                    Clear
                </button>
                <button onClick={onApply} className={`${button} ${buttonApply}`}>
                    Apply
                </button>
            </div>
        </components.Menu>
    );
};

const CustomOption = (props: ExtendedOptionsProps) => {
    const {
        label,
        data: { imgUrl, value },
        imageWithLabel,
        baseImageProps,
        isSelected,
        isMulti,
        isRadio,
        localValues,
    } = props;
    let isLocalSelected = isMulti ? false : isSelected;
    const valueToString = value.toString();

    if (isMulti && Array.isArray(localValues)) {
        isLocalSelected = localValues.includes(valueToString);
    }

    return (
        <components.Option {...props}>
            {imgUrl ? (
                <>
                    <BaseImage
                        className="react-select__option-image"
                        image={undefined}
                        url={imgUrl}
                        alt={label}
                        objectFit="contain"
                        {...baseImageProps}
                    />
                    {imageWithLabel && <span>{label}</span>}
                </>
            ) : isMulti || isRadio ? (
                <div
                    className={`${optionBox} ${isRadio ? radio : ''} ${
                        isLocalSelected ? selected : ''
                    }`}
                >
                    <span className={optionCheckbox}>
                        <CheckmarkIcon className={optionCheckmark} />
                    </span>{' '}
                    <span>{label}</span>
                </div>
            ) : (
                label
            )}
        </components.Option>
    );
};

const CustomSingleValue = (props: ExtendedSingleValueProps) => {
    const {
        data: { imgUrl, label },
        imageWithLabel,
        baseImageProps,
    } = props;
    return (
        <components.SingleValue {...props}>
            {imgUrl ? (
                <>
                    <BaseImage
                        className="react-select__value-image"
                        image={undefined}
                        url={imgUrl}
                        alt={label || ''}
                        objectFit="contain"
                        ratioClass={ratio}
                        {...baseImageProps}
                    />
                    {imageWithLabel && <span>{label}</span>}
                </>
            ) : (
                label
            )}
        </components.SingleValue>
    );
};

const CustomValueContainer = (props: ValueContainerProps) => {
    const count = props.getValue().length;

    return (
        <>
            <components.ValueContainer {...props} />
            {count > 0 && <span className={valueCount}>({count})</span>}
        </>
    );
};

function assertIsMultiValue(value: unknown): value is MultiValue<IOption> {
    return Array.isArray(value);
}

export default Select;
